import { useCallback, useSyncExternalStore } from "react";

const LS_KEY = "acweb_page_help_viewed";
const SYNC_EVENT = "acweb:page-help-viewed";

function getViewedPaths(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function persistViewedPaths(paths: Set<string>): void {
  localStorage.setItem(LS_KEY, JSON.stringify([...paths]));
}

let snapshot = getViewedPaths();

function subscribe(cb: () => void): () => void {
  const handler = () => {
    snapshot = getViewedPaths();
    cb();
  };
  window.addEventListener("storage", handler);
  window.addEventListener(SYNC_EVENT, handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(SYNC_EVENT, handler);
  };
}

function getSnapshot(): Set<string> {
  return snapshot;
}

export function usePageHelpViewed(path: string) {
  const viewedPaths = useSyncExternalStore(subscribe, getSnapshot);
  const viewed = viewedPaths.has(path);

  const markViewed = useCallback(() => {
    const current = getViewedPaths();
    if (current.has(path)) return;
    current.add(path);
    persistViewedPaths(current);
    snapshot = current;
    window.dispatchEvent(new Event(SYNC_EVENT));
  }, [path]);

  return { viewed, markViewed } as const;
}
